import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { brand } from '../config/brand';

const hexPattern =
  "url(\"data:image/svg+xml,%3Csvg width='56' height='100' viewBox='0 0 56 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M28 66L0 50V16l28-16 28 16v34L28 66zM28 0v16M0 50l28 16 28-16' fill='none' stroke='%230E5BA8' stroke-opacity='0.06'/%3E%3C/svg%3E\")";

const principles = [
  {
    title: 'Ultra-pure material science',
    body: 'We start from demineralised water and precisely characterised, high-grade raw materials — minimising the trace contaminants and fillers that cause streaking, residue and inconsistent performance.',
  },
  {
    title: 'Circular-by-design manufacturing',
    body: 'Concentrated formulations mean less water shipped, less packaging per litre of working solution, and a manufacturing philosophy oriented toward minimising waste at every step.',
  },
  {
    title: 'Asset-protective chemistry',
    body: 'Balanced pH systems and considered actives are designed to clean effectively while being gentler on the surfaces, fittings and equipment they touch — helping protect your capital assets over their service life.',
  },
  {
    title: 'Batch-traceable consistency',
    body: 'Under our ISO 9001:2015 quality system, every batch is made to the same specification and is traceable — so what performed last quarter performs the same today.',
  },
];

export default function MolecularPurityPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'The Molecular Purity Standard — Uclipse',
    url: `${brand.siteUrl}/molecular-purity`,
    description:
      'The Molecular Purity Standard is Uclipse’s framework for ultra-pure material science and circular-by-design manufacturing — engineered to run cleaner and protect enterprise assets.',
    isPartOf: { '@type': 'WebSite', name: 'Uclipse', url: `${brand.siteUrl}/` },
    about: 'Ultra-pure cleaning chemistry and circular manufacturing',
  };

  return (
    <>
      <Seo
        title="The Molecular Purity Standard"
        description="Beyond commoditised “green”. The Molecular Purity Standard is Uclipse’s framework for ultra-pure material science and circular-by-design manufacturing — engineered to run cleaner and protect enterprise assets."
        canonicalUrl={`${brand.siteUrl}/molecular-purity`}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <div className="pointer-events-none absolute inset-0 opacity-70" style={{ backgroundImage: hexPattern }} />
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand-100/60 blur-3xl" />
        <div className="container-page relative max-w-4xl py-16 md:py-24">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-brand-700 ring-1 ring-brand-100">
            Regenerative Purity
          </span>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight text-brand-800 md:text-5xl lg:text-6xl">
            The Molecular Purity Standard
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-600">
            “Eco-friendly” and “biodegradable” have become table stakes. We hold ourselves to a higher, more
            precise bar — one rooted in material science and circular manufacturing rather than marketing language.
          </p>
        </div>
      </section>

      {/* Beyond green */}
      <section className="border-y border-slate-100 bg-slate-50 py-16">
        <div className="container-page grid gap-8 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-7">
            <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-400">
              The commoditised claim
            </h2>
            <p className="mt-3 text-slate-600">
              Most chemical brands compete on the same diluted vocabulary — “green”, “natural”, “eco”. The terms say
              little about what is actually in the bottle, how consistently it performs, or what it does to your
              equipment over years of use.
            </p>
          </div>
          <div className="rounded-2xl border border-brand-200 bg-gradient-to-br from-brand-50 to-white p-7 shadow-card">
            <h2 className="font-display text-sm font-bold uppercase tracking-wide text-brand-700">
              The Molecular Purity Standard
            </h2>
            <p className="mt-3 text-slate-700">
              A defined, repeatable discipline: purer inputs, concentrated and circular formats, asset-protective
              chemistry, and batch-level traceability — so performance is engineered and verifiable, not asserted.
            </p>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="container-page py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">Four principles, one standard</h2>
          <p className="mt-3 text-slate-600">
            Every Uclipse formulation is developed against the same four commitments.
          </p>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {principles.map((p, i) => (
            <article key={p.title} className="card p-7">
              <div className="font-display text-sm font-bold text-brand-300">
                {String(i + 1).padStart(2, '0')}
              </div>
              <h3 className="mt-1 font-display text-lg font-semibold text-slate-900">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Asset protection callout */}
      <section className="bg-brand-800 py-16 text-white">
        <div className="container-page grid items-center gap-8 md:grid-cols-2">
          <div>
            <span className="text-sm font-semibold uppercase tracking-wide text-brand-200">
              Total cost of ownership
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">
              Purity is an asset-protection strategy
            </h2>
          </div>
          <p className="text-brand-50/85">
            The cheapest cleaner is rarely the lowest total cost. Aggressive, inconsistent chemistry can pit metal,
            haze stone, and degrade fittings — quietly compounding maintenance and replacement costs. Purer,
            asset-protective formulations are designed to keep surfaces and equipment performing longer, so the
            value shows up on your balance sheet, not just the invoice.
          </p>
        </div>
        <div className="container-page mt-10">
          <Link to="/precision-lab" className="btn bg-white text-brand-700 hover:bg-slate-100">
            Explore concierge engineering →
          </Link>
        </div>
      </section>
    </>
  );
}
